import { removeCurrentCrmQueryCache } from '@/lib/persistedCrmQueryCache';

/**
 * Boundary globale della sessione browser.
 *
 * Una risposta 401 non e' un normale stato vuoto: continuare a renderizzare
 * cache e drawer dopo che la sessione e' scaduta fa sembrare Martina attiva
 * mentre ogni fonte reale e' irraggiungibile. Tutti i fetch del browser
 * passano quindi qui, indipendentemente dal modulo chiamante.
 */
let authExpiryRedirectStarted = false;

export function isSafeAuthReturnTo(value: string | null | undefined): value is string {
  return typeof value === 'string'
    && value.startsWith('/')
    && !value.startsWith('//')
    && !value.startsWith('/login')
    && !value.startsWith('/auth/login');
}

function readTokenExpiry(token: string): number | null {
  try {
    const payloadBase64 = token.split('.')[1];
    if (!payloadBase64) return null;
    const normalized = payloadBase64.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized.padEnd(normalized.length + ((4 - (normalized.length % 4)) % 4), '=');
    const payload = JSON.parse(window.atob(padded)) as { exp?: number };
    return typeof payload.exp === 'number' ? payload.exp : null;
  } catch {
    return null;
  }
}

function isTokenPresentAndValid(token: string | null): boolean {
  if (!token) return false;
  const exp = readTokenExpiry(token);
  if (exp === null) return true;
  // Margine di 30 secondi per evitare logout su token prossimi alla scadenza.
  return exp * 1000 > Date.now() + 30_000;
}

export function handleUnauthorizedSession(): void {
  if (typeof window === 'undefined' || authExpiryRedirectStarted) return;

  const { pathname, search, hash } = window.location;
  if (pathname === '/login' || pathname === '/auth/login') return;

  authExpiryRedirectStarted = true;
  try {
    removeCurrentCrmQueryCache();
    window.localStorage.removeItem('auth_token');
  } catch {
    // Un browser con storage disabilitato deve comunque raggiungere il login.
  }

  const returnTo = `${pathname}${search}${hash}`;
  const loginUrl = isSafeAuthReturnTo(returnTo)
    ? `/login?returnTo=${encodeURIComponent(returnTo)}`
    : '/login';
  window.location.replace(loginUrl);
}

/** Installa una sola volta il gate per anche i fetch legacy diretti. */
export function installUnauthorizedResponseGuard(): void {
  if (typeof window === 'undefined' || (window as Window & { __experviserAuthGuardInstalled?: boolean }).__experviserAuthGuardInstalled) return;

  const originalFetch = window.fetch.bind(window);
  window.fetch = async (...args: Parameters<typeof fetch>): Promise<Response> => {
    const response = await originalFetch(...args);
    if (response.status === 401) {
      const token = typeof window !== 'undefined'
        ? window.localStorage.getItem('auth_token')
        : null;
      const [url] = args;
      if (isTokenPresentAndValid(token)) {
        // Il token esiste ed e' ancora valido: la 401 e' un falso positivo
        // (route non autenticata correttamente, WAF, race condition, ecc.).
        // Non buttiamo fuori l'utente: logghiamo l'endpoint per la diagnosi.
        console.warn('[AUTH GUARD] 401 with valid token; skipping forced logout.', {
          url: typeof url === 'string' ? url : url instanceof Request ? url.url : String(url),
        });
      } else {
        handleUnauthorizedSession();
      }
    }
    return response;
  };
  (window as Window & { __experviserAuthGuardInstalled?: boolean }).__experviserAuthGuardInstalled = true;
}
